import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Trophy, Flame, Check, X, Users } from "lucide-react";
import { toast } from "sonner";
import { CelebPortrait } from "../components/CelebPortrait";
import { StartButton } from "../components/StartButton";
import { useAuth } from "../components/AuthProvider";
import { playClick } from "../lib/sounds";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Landing page a friend hits when someone shares their Daily Challenge deep-link.
 * Shows the challenger's score + today's factor/pairing, then boots the friend
 * into today's Daily Smash flow. If the challenge is from a previous day, the
 * page is a read-only "You just missed it" state that redirects to today.
 */
export default function DailyChallengeInvite() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user, login } = useAuth();
  const [data, setData] = useState(null);
  const [group, setGroup] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/daily/challenge/${code}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => {
        if (!alive) return;
        setData(d);
        // Fire-and-forget accept tracking, then fetch group leaderboard.
        fetch(`${API}/api/daily/challenge/${code}/accept`, {
          method: "POST", credentials: "include",
        })
          .catch(() => {})
          .finally(() => {
            fetch(`${API}/api/daily/challenge/${code}/group`, { credentials: "include" })
              .then((r) => r.ok ? r.json() : null)
              .then((g) => alive && setGroup(g))
              .catch(() => {});
          });
      })
      .catch(() => alive && setErr("Challenge not found."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [code]);

  const accept = () => {
    playClick();
    // Persist code so /daily can show a comparison ribbon post-submit
    try { sessionStorage.setItem("cs.vsChallengeCode", code); } catch {}
    nav("/daily");
  };

  if (loading) {
    return <div className="min-h-screen spotlight grain scanlines pt-24 text-center text-[#FDFBF7]/60">Loading challenge…</div>;
  }

  if (err) {
    return (
      <div className="min-h-screen spotlight grain scanlines">
        <div className="max-w-md mx-auto px-5 pt-24 pb-nav text-center">
          <div className="font-display text-4xl">Challenge not found</div>
          <button onClick={() => nav("/daily")}
            className="mt-6 rounded-full bg-[#FF107A] text-[#FDFBF7] px-6 py-3 font-bold" data-testid="dci-goto-daily">
            Play today&rsquo;s Smash
          </button>
        </div>
      </div>
    );
  }

  const { challenger, factor, pairing, is_today, accepted_count, beaten_count, me, daily_date } = data;

  return (
    <div className="min-h-screen spotlight grain scanlines">
      <div className="max-w-md mx-auto px-5 pt-16 pb-nav">
        <button data-testid="dci-back-btn" onClick={() => nav("/home")}
          className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]">
          <ArrowLeft size={16} /> Home
        </button>

        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mt-4 relative overflow-hidden rounded-3xl border border-white/10 p-5"
          style={{
            background: "radial-gradient(circle at 15% 15%, rgba(255,215,0,0.28), transparent 55%)," +
                        "linear-gradient(160deg, #16141D 0%, #0A090D 100%)",
          }}
          data-testid="dci-hero"
        >
          <div className="text-[10px] tracking-[0.35em] uppercase font-bold text-[#FFD700]">
            <Trophy size={11} className="inline mr-1" /> You&rsquo;ve Been Challenged
          </div>
          <h1 className="font-display text-4xl leading-[0.9] tracking-tight mt-2">
            {challenger.name || "A friend"} scored{" "}
            <span className="text-[#FFD700]">{challenger.score}</span>
          </h1>
          <div className="mt-1 text-sm text-[#FDFBF7]/70">
            on Daily Smash #{data.smash_number} · <span className="text-[#00E5FF] font-semibold">{factor.label}</span>
          </div>
          {challenger.title && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#FF107A]/40 bg-[#FF107A]/10 px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-bold text-[#FF107A]">
              {challenger.title}
            </div>
          )}
        </motion.div>

        {/* Today's matchup preview */}
        <div className="mt-4 rounded-2xl border border-white/10 bg-black/30 p-4">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#FDFBF7]/60">Today&rsquo;s Matchup</div>
          <div className="mt-3 grid grid-cols-2 gap-3 items-center">
            <CelebPortrait name={pairing.a} size={110} />
            <CelebPortrait name={pairing.b} size={110} />
          </div>
        </div>

        {/* Social proof + status */}
        <div className="mt-3 flex gap-2 text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/60">
          <div className="flex-1 rounded-xl border border-white/10 bg-black/30 py-2 text-center">
            <div className="font-display text-lg text-[#00E5FF] tabular">{accepted_count}</div>
            <div>Accepted</div>
          </div>
          <div className="flex-1 rounded-xl border border-white/10 bg-black/30 py-2 text-center">
            <div className="font-display text-lg text-[#FF107A] tabular">{beaten_count}</div>
            <div>Beaten</div>
          </div>
        </div>

        {!is_today && (
          <div data-testid="dci-expired" className="mt-4 rounded-2xl border border-[#FF107A]/40 bg-[#FF107A]/10 p-4 text-sm">
            This challenge was for <span className="font-bold">{daily_date}</span>. The Smash Factor changes every day — play today&rsquo;s to keep your streak going.
          </div>
        )}

        {group?.show_group && (
          <div data-testid="dci-group-leaderboard"
            className="mt-4 rounded-2xl border border-[#FFD700]/40 bg-black/30 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] tracking-[0.35em] uppercase font-bold text-[#FFD700] flex items-center gap-2">
                <Users size={12} /> Group Smash · {group.played_count} playing
              </div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/50">
                Smash #{group.smash_number}
              </div>
            </div>
            <div className="space-y-1.5">
              {group.rows.slice(0, 8).map((r) => (
                <div key={r.user_id || r.rank}
                  data-testid={`dci-group-row-${r.rank}`}
                  className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${
                    r.is_challenger
                      ? "border-[#FF107A]/50 bg-[#FF107A]/10"
                      : "border-white/10 bg-black/40"
                  }`}
                >
                  <div className={`w-7 text-center font-display text-lg tabular ${
                    r.rank === 1 ? "text-[#FFD700]"
                    : r.rank === 2 ? "text-[#00E5FF]"
                    : r.rank === 3 ? "text-[#FF6B35]" : "text-[#FDFBF7]/60"
                  }`}>{r.rank}</div>
                  {r.picture ? (
                    <img src={r.picture} alt="" className="w-8 h-8 rounded-full border border-white/20" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#FF107A]/20 border border-[#FF107A]/40 flex items-center justify-center text-xs font-bold">
                      {r.name?.[0]?.toUpperCase() || "?"}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#FDFBF7] truncate">
                      {r.name} {r.is_challenger && (
                        <span className="text-[9px] tracking-[0.2em] uppercase text-[#FF107A] ml-1">Challenger</span>
                      )}
                    </div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/50">{r.title}</div>
                  </div>
                  <div className="font-display text-lg tabular text-[#FFD700]">{r.score}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {is_today && me?.played && (
          <div data-testid="dci-result"
            className={`mt-4 rounded-2xl border-2 p-4 ${
              me.won ? "border-[#22C55E] bg-[#22C55E]/10" : "border-[#FF107A]/50 bg-[#FF107A]/10"
            }`}
          >
            <div className="flex items-center gap-2">
              {me.won ? <Check size={20} className="text-[#22C55E]" /> : <X size={20} className="text-[#FF107A]" />}
              <div className="font-display text-2xl">
                {me.won ? "You beat them!" : "So close…"}
              </div>
            </div>
            <div className="text-sm mt-1 text-[#FDFBF7]/80">
              You scored <b>{me.score}</b> · they scored <b>{challenger.score}</b>
            </div>
          </div>
        )}

        {/* CTA */}
        {is_today && !me?.played && (
          <StartButton
            onClick={accept}
            testId="dci-accept-btn"
            label={me ? "Beat their score" : "Play & Beat Them"}
          />
        )}
        {is_today && me?.played && (
          <button data-testid="dci-see-leaderboard" onClick={() => nav("/leaderboards/daily")}
            className="mt-6 w-full rounded-full border border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700] py-3 font-bold flex items-center justify-center gap-2">
            <Trophy size={16} /> See the leaderboard
          </button>
        )}
        {!is_today && (
          <StartButton
            onClick={() => { playClick(); nav("/daily"); }}
            testId="dci-goto-today-btn"
            label="Play Today's Smash"
          />
        )}

        {!user && (
          <button onClick={login} data-testid="dci-signin-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FDFBF7] text-[#0A090D] px-6 py-3 font-bold tracking-wide">
            <Users size={16} /> Sign in to save your score
          </button>
        )}

        <div className="mt-6 text-center text-[10px] tracking-[0.3em] uppercase text-[#FDFBF7]/40">
          <Flame size={11} className="inline mr-1" /> Every day. Every factor. One shot.
        </div>
      </div>
    </div>
  );
}
